/*
 * ez-lightbox — the REUSABLE media viewer MODAL (design_system.md §6 3.3 Lightbox, inheriting the
 * §6 3.1 overlay grammar). One vanilla custom element, defined once, no framework and no external
 * slider / video / animation library (§A0 / §A5 / §A7).
 *
 * [2026-07-23 v3] TWO OWNER RULINGS REWROTE THIS FILE, and both removed machinery:
 *
 *   1. IT IS A MODAL, NOT A FULL-BLEED VIEWER. Translucent backdrop, the page still visible behind
 *      it, click-anywhere-outside closes. "so user doesn't feel blocked."
 *
 *   2. NO HORIZONTAL TRANSPORT. One tile opens ONE panel and that is the only panel. This element
 *      no longer extends window.EzCarousel — there is no snap track, no arrows, no keyboard
 *      stepping, no "N / M" counter. Contract 3.3's "reuses the carousel finite-snap grammar" is
 *      formally superseded (a 9:16 shoppable panel carrying its own horizontal product strip is a
 *      different object from a photo gallery, and a horizontal swipe on the panel would fight the
 *      strip's own scroll). Dropping the engine is also what makes the reported "it scrolls from
 *      panel 1 across to the one I opened" impossible: only the opened panel is ever adopted, so
 *      there is nothing to travel through.
 *
 * THE FIVE OVERLAY A11Y NON-NEGOTIABLES (§6 3.1, inherited by 3.3) all survive that:
 *   focus-trap while open · ESC closes · backdrop-click closes · body scroll-lock while open ·
 *   return-focus to the trigger on close.
 *
 * BYTES: a panel's <video> holds its source in data-ez-lb-src, and panels live in inert <template>
 * nodes until adopted, so a lightbox that is never opened costs ZERO video bytes AND zero product
 * imagery. The lightbox video is the ONE video allowed to pull real bytes (M2, PK phones).
 *
 * MARKUP CONTRACT — snippets/ez-lightbox-styles.liquid (structure) + ez-lightbox-chrome.liquid
 * (chrome). Hooks consumed here:
 *   [data-ez-lb-track]            the adopt target (exactly one panel at a time)
 *   [data-ez-lb-panel="<key>"]    the panel; `key` matches its opener's value
 *   [data-ez-lb-panel-tpl="<key>"] the inert <template> a tile block emits, adopted from
 *   [data-ez-lb-backdrop]         click closes (so does any click outside the stage)
 *   [data-ez-lb-close]            click closes
 *   [data-ez-lb-video]            the panel video; sources carry data-ez-lb-src
 *   [data-ez-lb-playpause]        the ONE control — the whole frame, centred glyph
 *   [data-ez-lb-progress]         the non-interactive progress fill (transform: scaleX)
 *   [data-ez-lb-mute]             optional sound button
 *
 * PUBLIC API: el.open(key[, trigger[, fallbackIndex]]) / el.close(). Dispatches bubbling
 * `ez-lightbox:open` / `ez-lightbox:close` so a hosting section can pause its own inline previews.
 */
(function () {
  if (customElements.get('ez-lightbox')) return;

  var FOCUSABLE = 'a[href],button:not([disabled]),input:not([disabled]),select:not([disabled]),' +
                  'textarea:not([disabled]),video[controls],[tabindex]:not([tabindex="-1"])';

  class EzLightbox extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('[data-ez-lb-track]');
      this.panel = null;
      this._key = null;

      var self = this;
      this.addEventListener('click', function (e) {
        if (e.target.closest('[data-ez-lb-close]') || e.target.closest('[data-ez-lb-backdrop]')) {
          self.close();
          return;
        }
        var pp = e.target.closest('[data-ez-lb-playpause]');
        if (pp) { self._togglePlay(); return; }
        var mute = e.target.closest('[data-ez-lb-mute]');
        if (mute) { self._toggleMute(); return; }
        // CLICK-OUTSIDE-THE-STAGE closes. The modal is deliberately smaller than the viewport now,
        // so the visible surround is a real exit and must behave like the backdrop under it —
        // otherwise the buyer sees the page through the scrim, clicks it, and nothing happens.
        if (!e.target.closest('.ez-lb__stage')) self.close();
      });

      this.addEventListener('keydown', function (e) {
        if (e.key === 'Escape') { e.preventDefault(); self.close(); return; }
        if (e.key === 'Tab') self._trapTab(e);
        // No ArrowLeft/ArrowRight: there is no second panel to step to.
      });

      // A tab going hidden must never leave audio/video running.
      this._onVis = function () { if (document.hidden) self._pause(); };
      document.addEventListener('visibilitychange', this._onVis);
    }

    disconnectedCallback() {
      document.removeEventListener('visibilitychange', this._onVis);
      this._unlockScroll();
    }

    /* ---- panel adoption --------------------------------------------------------------- */

    // Adopt EXACTLY the requested panel from the tile blocks' inert <template> nodes.
    //
    // WHY THE PANELS ARE NOT SERVER-RENDERED IN THE SECTION: a section that emits its children with
    // {% content_for 'blocks' %} cannot read those children's settings back off `section.blocks` —
    // the ids resolve and .size resolves, but every block.settings.* comes back blank (bug-157). So
    // each block renders its own panel, in its own scope, into a <template>; this hoists the one
    // that was asked for.
    //
    // ONE panel, not all of them: that is what removes the horizontal traverse the owner reported,
    // and it means an 8-tile row never builds 8 panels' worth of DOM or fetches 8 panels' worth of
    // product imagery to show one.
    _adopt(key, fallbackIndex) {
      if (!this.track) return null;
      if (this._key === key && this.panel) return this.panel;

      var srcId = this.dataset.ezLbPanelsFrom;
      var src = srcId ? document.getElementById(srcId) : null;
      if (!src) return this.panel;

      var tpls = src.querySelectorAll('template[data-ez-lb-panel-tpl]');
      var tpl = null;
      for (var i = 0; i < tpls.length; i++) {
        if (tpls[i].getAttribute('data-ez-lb-panel-tpl') === String(key)) { tpl = tpls[i]; break; }
      }
      // Key MISS -> the opener's ordinal. A tile and its panel are both written by the block file
      // so they cannot drift today; this is the guard for the day someone changes that.
      if (!tpl && typeof fallbackIndex === 'number' && fallbackIndex >= 0 && fallbackIndex < tpls.length) {
        tpl = tpls[fallbackIndex];
      }
      if (!tpl) return this.panel;

      this._pause();
      this.track.textContent = '';
      this.track.appendChild(tpl.content.cloneNode(true));
      this.panel = this.track.querySelector('[data-ez-lb-panel]');
      this._key = key;

      // The mute and CTA controls are SECTION settings but the panel is built in BLOCK scope, so
      // both are always emitted and stripped here. Done before the panel is ever painted, so there
      // is no flash. Neither does anything without JS, so gating them in JS costs nothing real.
      if (this.hasAttribute('data-ez-lb-no-mute')) this._strip('[data-ez-lb-mute]');
      if (this.hasAttribute('data-ez-lb-no-cta')) this._strip('.ez-lb__ctas');

      return this.panel;
    }

    _strip(sel) {
      if (!this.panel) return;
      var nodes = this.panel.querySelectorAll(sel);
      for (var i = 0; i < nodes.length; i++) nodes[i].parentNode.removeChild(nodes[i]);
    }

    /* ---- open / close ---------------------------------------------------------------- */

    open(key, trigger, fallbackIndex) {
      this._trigger = trigger || document.activeElement;
      this.hidden = false;
      this._lockScroll();

      var panel = this._adopt(key, fallbackIndex);
      if (panel) this._start(panel);

      var dialog = this.querySelector('[data-ez-lb-dialog]') || this;
      var first = dialog.querySelector('[data-ez-lb-close]') || dialog;
      if (first && first.focus) first.focus({ preventScroll: true });

      this.dispatchEvent(new CustomEvent('ez-lightbox:open', { bubbles: true }));
    }

    close() {
      this._pause();
      this.hidden = true;
      this._unlockScroll();
      if (this._trigger && this._trigger.focus) this._trigger.focus({ preventScroll: true });
      this._trigger = null;
      this.dispatchEvent(new CustomEvent('ez-lightbox:close', { bubbles: true }));
    }

    /* ---- the five non-negotiables: scroll-lock + focus-trap --------------------------- */

    _lockScroll() {
      if (this._locked) return;
      this._scrollY = window.scrollY || window.pageYOffset || 0;
      var b = document.body;
      this._prev = { position: b.style.position, top: b.style.top, width: b.style.width };
      b.style.position = 'fixed';
      b.style.top = '-' + this._scrollY + 'px';
      b.style.width = '100%';
      this._locked = true;
    }

    _unlockScroll() {
      if (!this._locked) return;
      var b = document.body;
      b.style.position = this._prev.position;
      b.style.top = this._prev.top;
      b.style.width = this._prev.width;

      // INSTANT restore, and the neutralisation is LOAD-BEARING, not tidiness. assets/base.css:34
      // sets `html { scroll-behavior: smooth }`, and per CSSOM a programmatic scroll INHERITS the
      // scrolling element's behavior — so a bare window.scrollTo(0, y) ANIMATES. That is the
      // reported bug exactly: closing the lightbox made the page visibly travel from the top back
      // down to where the tile was. Same trap the infinite banner documents on its wrap reposition
      // and the lightbox used to document on its panel jump: whenever a scroll must not be seen,
      // say so explicitly rather than trusting the default.
      var root = document.documentElement;
      var prevBehavior = root.style.scrollBehavior;
      root.style.scrollBehavior = 'auto';
      window.scrollTo({ top: this._scrollY, left: 0, behavior: 'instant' });
      root.style.scrollBehavior = prevBehavior;

      this._locked = false;
    }

    _trapTab(e) {
      var scope = this.querySelector('[data-ez-lb-dialog]') || this;
      var nodes = Array.prototype.slice.call(scope.querySelectorAll(FOCUSABLE))
        .filter(function (n) { return n.offsetParent !== null || n === document.activeElement; });
      if (!nodes.length) return;
      var first = nodes[0];
      var last = nodes[nodes.length - 1];
      if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
    }

    /* ---- video ------------------------------------------------------------------------ */

    _video() {
      return this.panel ? this.panel.querySelector('[data-ez-lb-video]') : null;
    }

    _start(panel) {
      var v = panel.querySelector('[data-ez-lb-video]');
      if (!v) return;
      if (!v.dataset.ezLbReady) {
        var posterDesktop = v.getAttribute('data-ez-lb-poster-desktop');
        var posterMobile = v.getAttribute('data-ez-lb-poster-mobile');
        var poster = posterMobile && window.matchMedia('(max-width: 749px)').matches
          ? posterMobile
          : posterDesktop;
        if (poster) v.poster = poster;
        var srcs = v.querySelectorAll('source[data-ez-lb-src]');
        for (var i = 0; i < srcs.length; i++) srcs[i].src = srcs[i].getAttribute('data-ez-lb-src');
        v.dataset.ezLbReady = '1';
        this._wire(panel, v);
        v.load();
      }
      panel.classList.remove('is-paused');
      var p = v.play();
      if (p && p.catch) p.catch(function () { panel.classList.add('is-paused'); });
    }

    _wire(panel, v) {
      var self = this;
      v.addEventListener('timeupdate', function () { self._paint(panel, v); self._syncMute(panel, v); });
      v.addEventListener('loadedmetadata', function () { self._paint(panel, v); self._syncMute(panel, v); });
      v.addEventListener('play', function () { panel.classList.remove('is-paused'); self._label(panel, false); });
      v.addEventListener('pause', function () { panel.classList.add('is-paused'); self._label(panel, true); });
      v.addEventListener('ended', function () { panel.classList.add('is-paused'); self._label(panel, true); });
      // A source that fails to load must SAY so: show the play affordance rather than sitting as a
      // silent box the visitor cannot tell apart from a slow network.
      v.addEventListener('error', function () { panel.classList.add('is-paused'); self._label(panel, true); });
    }

    // The progress INDICATOR. scaleX, never width — §7.3 bars animating layout properties.
    _paint(panel, v) {
      var fill = panel.querySelector('[data-ez-lb-progress]');
      if (!fill) return;
      var d = isFinite(v.duration) && v.duration ? v.duration : 0;
      var pct = d ? (v.currentTime / d) : 0;
      if (pct < 0) pct = 0;
      if (pct > 1) pct = 1;
      fill.style.transform = 'scaleX(' + pct.toFixed(4) + ')';
    }

    _label(panel, paused) {
      var btn = panel.querySelector('[data-ez-lb-playpause]');
      if (btn) btn.setAttribute('aria-label', paused ? 'Play video' : 'Pause video');
    }

    // A sound button on silent footage is a control that does nothing, and a dead control reads as
    // a broken shop. The merchant toggle decides whether the button survives adoption at all; this
    // is the second guard — if the browser will TELL us the track carries no audio, hide it anyway.
    //
    // Deliberately one-directional: hide only on PROOF of silence, never on absence of evidence. No
    // engine reports this uniformly (Firefox has mozHasAudio, Chromium's decoded-byte counter only
    // becomes meaningful after playback starts, audioTracks is patchy), so a browser that tells us
    // nothing leaves the merchant's choice standing. Hiding also removes it from the focus-trap for
    // free — the trap filters on offsetParent, and a [hidden] element has none.
    _syncMute(panel, v) {
      var btn = panel.querySelector('[data-ez-lb-mute]');
      if (!btn || btn.hidden) return;
      var silent = null;
      if (typeof v.mozHasAudio === 'boolean') {
        silent = !v.mozHasAudio;
      } else if (v.audioTracks && typeof v.audioTracks.length === 'number') {
        silent = v.audioTracks.length === 0;
      } else if (typeof v.webkitAudioDecodedByteCount === 'number' && v.currentTime > 0.25) {
        silent = v.webkitAudioDecodedByteCount === 0;
      }
      if (silent === true) {
        btn.hidden = true;
        panel.classList.remove('is-unmuted');
        if (!v.muted) v.muted = true;
      }
    }

    _togglePlay() {
      var v = this._video();
      if (!v) return;
      if (v.paused) { var p = v.play(); if (p && p.catch) p.catch(function () {}); }
      else v.pause();
    }

    _toggleMute() {
      var v = this._video();
      if (!v || !this.panel) return;
      v.muted = !v.muted;
      this.panel.classList.toggle('is-unmuted', !v.muted);
      var btn = this.panel.querySelector('[data-ez-lb-mute]');
      if (btn) btn.setAttribute('aria-label', v.muted ? 'Unmute video' : 'Mute video');
    }

    _pause() {
      var v = this._video();
      if (v) { try { v.pause(); } catch (e) {} }
    }
  }

  window.EzLightbox = EzLightbox;
  customElements.define('ez-lightbox', EzLightbox);
})();
